import * as Cesium from 'cesium';
import { twoline2satrec, type SatRec } from 'satellite.js';
import { computeGroundTrack, periodMinutes } from '../passes/computeGroundTrack';
import {
  loadRoster,
  sunSynchSegmentAlpha,
  type SunSynchEntry,
} from './sunSynchLoader';
import type { PointPrimitiveLayer } from './PointPrimitiveLayer';

/**
 * Phase 9 chunk 3 — MarqueeRibbonLayer.
 *
 * Renders a dim ground-track ribbon for each operational sun-synch
 * roster entry (locked at 9 sats per docs/phase9.md § II row 5), so
 * users can see the dawn/dusk terminator-following pattern at a
 * glance — regardless of which satellite is selected.
 *
 * Mirrors {@link OrbitRibbonLayer}'s rendering shape:
 *   - one Cesium PolylineCollection (cheap to add/remove)
 *   - ±0.5 orbits centered on the clock time (no wraparound clutter)
 *   - 30-segment alpha falloff per-ribbon
 *   - past dimmer than future
 *
 * Differences from the selection ribbon:
 *   - multiple ribbons co-exist, one per roster entry
 *   - alpha curve runs through {@link sunSynchSegmentAlpha} (lower
 *     max than chunk-2's white selection ribbon)
 *   - re-render throttled to once per 30 s of clock time (the
 *     selection ribbon uses 1/30 of the period, which would be ~3
 *     min for these polar orbiters — too sluggish to feel responsive
 *     during a time scrub)
 *
 * Satrec construction is lazy: the first time a satellite's TLE is
 * available from {@link PointPrimitiveLayer.getTle}, we cache the
 * satrec on this layer; subsequent re-renders reuse it. Entries
 * whose TLEs aren't in the global catalog yet are silently skipped
 * (the SPA boot occasionally races the layer's first update against
 * the worker's load).
 */
export class MarqueeRibbonLayer {
  private readonly collection: Cesium.PolylineCollection;
  private readonly roster: SunSynchEntry[];
  /** norad → cached satrec built from the latest TLE lines. */
  private readonly satrecs = new Map<number, SatRec>();
  private visible = false;
  private lastRenderTimeMs = 0;
  private centerTimeMs = 0;
  private tleSource: PointPrimitiveLayer | null = null;

  constructor(private readonly scene: Cesium.Scene) {
    this.collection = scene.primitives.add(new Cesium.PolylineCollection());
    this.roster = loadRoster();
  }

  /** Hand in the catalog layer once it's loaded so satrec construction can run on-demand. */
  setTleSource(source: PointPrimitiveLayer): void {
    this.tleSource = source;
  }

  /** Returns the operational roster (read-only) — useful for tests + debug overlays. */
  getRoster(): readonly SunSynchEntry[] {
    return this.roster;
  }

  /**
   * Show or hide the layer. When showing, we kick a render immediately
   * with the current clock time so the user doesn't wait for the next
   * tick. When hiding, the PolylineCollection is cleared.
   */
  setVisible(visible: boolean, currentTimeMs: number = Date.now()): void {
    if (visible === this.visible) return;
    this.visible = visible;
    if (visible) {
      this.centerTimeMs = currentTimeMs;
      this.render();
    } else {
      this.collection.removeAll();
    }
  }

  /**
   * Tick handler — called on every Cesium clock event. Re-renders
   * once per 30 s of clock time per docs/phase9.md § III chunk 3.
   * Cheap when invisible.
   */
  update(currentTimeMs: number): void {
    if (!this.visible) return;
    const delta = Math.abs(currentTimeMs - this.lastRenderTimeMs);
    if (delta < THROTTLE_MS) return;
    this.centerTimeMs = currentTimeMs;
    this.render();
  }

  destroy(): void {
    if (!this.scene.isDestroyed()) {
      this.scene.primitives.remove(this.collection);
    }
    this.satrecs.clear();
  }

  // ─── Internals ─────────────────────────────────────────────────────────

  private render(): void {
    this.collection.removeAll();
    const tleSource = this.tleSource;
    if (tleSource === null) {
      this.lastRenderTimeMs = this.centerTimeMs;
      return;
    }

    for (const entry of this.roster) {
      const satrec = this.getOrBuildSatrec(entry.norad);
      if (satrec === null) continue;
      this.renderOne(satrec);
    }
    this.lastRenderTimeMs = this.centerTimeMs;
  }

  private renderOne(satrec: SatRec): void {
    const points = computeGroundTrack({
      satrec,
      centerTimeMs: this.centerTimeMs,
      pastOrbits: PAST_ORBITS,
      futureOrbits: FUTURE_ORBITS,
      samplesPerOrbit: SAMPLES_PER_ORBIT,
    });
    if (points.length < 2) return;

    const periodMs = periodMinutes(satrec) * 60_000;
    const halfWindowMs = periodMs * Math.max(PAST_ORBITS, FUTURE_ORBITS);
    const segmentCount = Math.min(SEGMENT_COUNT, Math.floor(points.length / 2));
    const samplesPerSegment = Math.ceil(points.length / segmentCount);

    for (let s = 0; s < segmentCount; s++) {
      const from = s * samplesPerSegment;
      const to   = Math.min((s + 1) * samplesPerSegment + 1, points.length);
      if (to - from < 2) continue;

      const slice = points.slice(from, to);
      const positions: Cesium.Cartesian3[] = [];
      for (const p of slice) {
        if (Number.isFinite(p.longitudeDeg) && Number.isFinite(p.latitudeDeg)) {
          positions.push(Cesium.Cartesian3.fromDegrees(p.longitudeDeg, p.latitudeDeg, 0));
        }
      }
      if (positions.length < 2) continue;

      const segmentCenterTimeMs = (slice[0].timeMs + slice[slice.length - 1].timeMs) / 2;
      const alpha = sunSynchSegmentAlpha(segmentCenterTimeMs - this.centerTimeMs, halfWindowMs);
      const color = Cesium.Color.fromCssColorString(RIBBON_HUE).withAlpha(alpha);
      this.collection.add({
        positions,
        width: 1.5,
        material: Cesium.Material.fromType('Color', { color }),
      });
    }
  }

  /**
   * Build a satrec for `norad` on first use; cache it for subsequent
   * frames. Returns null if the TLE isn't in the global catalog yet
   * (e.g. before the worker has loaded) or if parsing fails.
   */
  private getOrBuildSatrec(norad: number): SatRec | null {
    if (this.tleSource === null) return null;
    const cached = this.satrecs.get(norad);
    if (cached !== undefined) return cached;
    const tle = this.tleSource.getTle(norad);
    if (tle === null) return null;
    try {
      const satrec = twoline2satrec(tle.line1, tle.line2);
      // satellite.js sets error != 0 on malformed input.
      if (satrec.error !== 0) return null;
      this.satrecs.set(norad, satrec);
      return satrec;
    } catch {
      return null;
    }
  }
}

const PAST_ORBITS         = 0.5;
const FUTURE_ORBITS       = 0.5;
const SAMPLES_PER_ORBIT   = 180;
const SEGMENT_COUNT       = 30;
const THROTTLE_MS         = 30_000;
const RIBBON_HUE          = '#7cc282'; // soft green — matches GNSS-GLONASS swatch family but distinct from #ffffff (selection) and #ffb700 (secondary)
